import React from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import CtaLinkList from '@/components/organisms/cta-link-list/cta-link-list';
import TravelPlan from '@/components/organisms/travel-plan/travel-plan';
import Column from '@/components/utilities/column/column';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import Row from '@/components/utilities/row/row';
import styles from './travel-plan-modal.module.scss';

export interface TravelPlanModalProps {
  /** An array of links to show */
  ctaLinks?: Content.Components['TravelPlanModuleHeadlessDto']['ctaLinks'];
  /** The text to show as a headline */
  headline?:
    | Content.Components['TravelPlanModuleHeadlessDto']['headline']
    | null;
  /** Indicates whether the data is being fetched */
  isLoading?: boolean;
  /** Whether the lightbox is open or not */
  isOpen?: boolean;
  /** The callback to close the modal */
  onClose?: (value: boolean) => void;
  /** The text to show as a sub headline */
  subHeadline?: Content.Components['TravelPlanModuleHeadlessDto']['subHeadline'];
  /** The text to show as a description */
  text?: Content.Components['TravelPlanModuleHeadlessDto']['text'];
  /** The travel plan information */
  travelPlan?:
    | Content.Components['TravelPlanModuleDto']['travelPlan']
    | Content.Components['FlightSummaryModuleDto']['travelSummaryDetailsDto']['travelPlan'];
}

const TravelPlanModal = ({
  ctaLinks = [],
  headline,
  isLoading = false,
  isOpen = false,
  onClose,
  subHeadline,
  text,
  travelPlan,
}: TravelPlanModalProps) => {
  const renderTravelPlan = () => {
    if (isLoading) {
      return (
        <React.Fragment>
          <Row className={styles.tablesRow}>
            <Column width={{ tablet: 6 }}>
              <SkeletonLoader
                className={styles.skeleton}
                height="176px"
                width="100%"
              />
            </Column>

            <Column width={{ tablet: 6 }}>
              <SkeletonLoader
                className={styles.skeleton}
                height="176px"
                width="100%"
              />
            </Column>
          </Row>
        </React.Fragment>
      );
    }

    if (!travelPlan) return null;

    return <TravelPlan travelPlan={travelPlan} />;
  };

  return (
    <Lightbox isOpen={isOpen} onClose={() => onClose?.(false)} size="medium">
      <div className={styles.component}>
        <div className={styles.container}>
          {(ctaLinks.length > 0 || headline || subHeadline || text) && (
            <Row>
              <Column className={styles.headlineColumn} width={{ laptop: 6 }}>
                {headline && (
                  <Typography
                    className={styles.headline}
                    tag="h2"
                    tagStyle="displayMedium"
                  >
                    {headline}
                  </Typography>
                )}

                {subHeadline && (
                  <Typography
                    className={styles.subHeadline}
                    tag="h3"
                    tagStyle="bodyLargeBold"
                  >
                    {subHeadline}
                  </Typography>
                )}

                {text && <RichText text={text} />}

                {ctaLinks.length > 0 && <CtaLinkList items={ctaLinks} />}
              </Column>
            </Row>
          )}

          {renderTravelPlan()}
        </div>
      </div>
    </Lightbox>
  );
};

TravelPlanModal.displayName = 'TravelPlanModal';

export default TravelPlanModal;
